import React, { createContext, useContext, useState, useCallback } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextValue {
    showToast: (message: string, type: ToastType) => void;
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Icons for each toast type
const ToastIcon = ({ type }: { type: ToastType }) => {
    if (type === 'success') {
        return (
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
            </div>
        );
    }
    if (type === 'error') {
        return (
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </div>
        );
    }
    return (
        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        </div>
    );
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType) => {
        const id = Math.random().toString(36).substring(7);
        const toast: Toast = { id, message, type };

        setToasts(prev => [...prev, toast]);

        // Auto-dismiss after 4 seconds (slightly longer for readability)
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    }, []);

    const success = useCallback((message: string) => showToast(message, 'success'), [showToast]);
    const error = useCallback((message: string) => showToast(message, 'error'), [showToast]);
    const info = useCallback((message: string) => showToast(message, 'info'), [showToast]);

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    const getToastStyles = (type: ToastType) => {
        switch (type) {
            case 'success':
                return 'bg-green-600 text-white shadow-lg shadow-green-500/50';
            case 'error':
                return 'bg-red-600 text-white shadow-lg shadow-red-500/50';
            case 'info':
                return 'bg-blue-600 text-white shadow-lg shadow-blue-500/50';
            default:
                return 'bg-gray-800 text-white shadow-lg';
        }
    };

    return (
        <ToastContext.Provider value={{ showToast, success, error, info }}>
            {children}
            {/* Desktop: bottom-right corner, Mobile: bottom full-width */}
            <div className="fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-3 md:min-w-[380px] md:max-w-md w-full md:w-auto px-4 md:px-0 pointer-events-none">
                {toasts.map((toast, index) => (
                    <div
                        key={toast.id}
                        className={`
                            flex items-center gap-3 px-4 py-3 rounded-lg
                            ${getToastStyles(toast.type)}
                            animate-toast-slide-up
                            backdrop-blur-sm
                            pointer-events-auto
                            transform transition-all duration-300
                        `}
                        style={{
                            animationDelay: `${index * 50}ms`
                        }}
                    >
                        <ToastIcon type={toast.type} />
                        <div className="flex-1 font-medium text-sm">{toast.message}</div>
                        <button
                            onClick={() => removeToast(toast.id)}
                            className="flex-shrink-0 ml-2 text-white/80 hover:text-white transition-colors"
                            aria-label="Close notification"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
}
